import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Mic, MicOff, Play, Square, Upload, CheckCircle, AlertTriangle, 
  AlertCircle, ChevronRight, RefreshCw, Volume2, Clock, BookOpen,
  Activity, Award, FileText, ArrowLeft, Globe, Pause, Zap
} from 'lucide-react';
import Navbar from '@/components/Navbar';
import { api } from '@/utils/api';
import { useLanguage } from '@/LanguageContext';

const SpeechAnalysis = ({ user, onLogout }) => {
  const { childId } = useParams();
  const navigate = useNavigate();
  const { t } = useLanguage();
  
  const [loading, setLoading] = useState(true);
  const [child, setChild] = useState(null);
  const [activities, setActivities] = useState([]);
  const [milestones, setMilestones] = useState(null);
  const [supportedLanguages, setSupportedLanguages] = useState({});
  const [selectedLanguage, setSelectedLanguage] = useState('en');
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [session, setSession] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioBlob, setAudioBlob] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [step, setStep] = useState('select'); // select, record, processing, result
  
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerRef = useRef(null);

  useEffect(() => {
    fetchActivities();
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [childId]);

  const fetchActivities = async () => {
    try {
      setLoading(true);
      const res = await api.getSpeechActivities(childId);
      setChild({
        id: childId,
        name: res.data.child_name,
        age_months: res.data.age_months
      });
      setActivities(res.data.activities);
      setMilestones(res.data.expected_milestones);
      setSupportedLanguages(res.data.supported_languages || {});
    } catch (error) {
      console.error('Error fetching activities:', error);
      setError('Failed to load activities');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectActivity = async (activity) => {
    setSelectedActivity(activity);
    setError(null);
    
    try {
      // Create session with selected language
      const res = await api.createSpeechSession(childId, activity.id, selectedLanguage);
      setSession(res.data);
      setStep('record');
    } catch (error) {
      console.error('Error creating session:', error);
      setError('Failed to start session');
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream);
      audioChunksRef.current = [];
      
      mediaRecorderRef.current.ondataavailable = (event) => {
        audioChunksRef.current.push(event.data);
      };
      
      mediaRecorderRef.current.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        setAudioBlob(audioBlob);
        stream.getTracks().forEach(track => track.stop());
      };
      
      mediaRecorderRef.current.start();
      setIsRecording(true);
      setRecordingTime(0);
      
      // Start timer
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
      
    } catch (error) {
      console.error('Error starting recording:', error);
      setError('Could not access microphone. Please check permissions.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    }
  };

  const uploadAndAnalyze = async () => {
    if (!audioBlob || !session) return;
    
    setProcessing(true);
    setStep('processing');
    setError(null);
    
    try {
      // Convert blob to file
      const audioFile = new File([audioBlob], 'speech_recording.webm', { type: 'audio/webm' });
      
      // Upload and analyze
      const res = await api.uploadSpeechAudio(session.session_id, audioFile);
      setResult(res.data);
      setStep('result');
      
    } catch (error) {
      console.error('Error analyzing speech:', error);
      setError(error.response?.data?.detail || 'Analysis failed. Please try again.');
      setStep('record');
    } finally {
      setProcessing(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getRiskColor = (risk) => {
    switch (risk) {
      case 'high': return 'text-red-600 bg-red-100';
      case 'moderate': return 'text-yellow-600 bg-yellow-100';
      default: return 'text-green-600 bg-green-100';
    }
  };

  const getRiskIcon = (risk) => {
    switch (risk) {
      case 'high': return <AlertTriangle className="text-red-500" size={24} />;
      case 'moderate': return <AlertCircle className="text-yellow-500" size={24} />;
      default: return <CheckCircle className="text-green-500" size={24} />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAF9]">
        <Navbar user={user} onLogout={onLogout} />
        <div className="flex items-center justify-center h-96">
          <RefreshCw size={32} className="animate-spin text-[#0F4C81]" />
        </div>
      </div>
    );
  }

  return (
    <>
    <div className="min-h-screen bg-[#FAFAF9]" data-testid="speech-analysis-page">
      <Navbar user={user} onLogout={onLogout} />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <button 
            onClick={() => navigate(`/child/${childId}`)}
            className="flex items-center text-[#6B7280] hover:text-[#0F4C81] mb-4"
          >
            <ArrowLeft size={20} className="mr-2" />
            Back to Child Profile
          </button>
          
          <div className="flex items-center space-x-4">
            <div className="bg-gradient-to-br from-[#8B5CF6] to-[#6D28D9] p-3 rounded-2xl shadow-lg">
              <Mic className="text-white" size={32} />
            </div>
            <div>
              <h1 className="text-3xl md:text-4xl font-bold text-[#111827] font-['Nunito']" data-testid="page-title">
                Speech Analysis
              </h1>
              <p className="text-base text-[#6B7280]">
                {child?.name} • {child?.age_months} months old
              </p>
            </div>
          </div>
        </div>

        {/* Error Banner */}
        {error && (
          <div className="mb-6 bg-red-50 border-2 border-red-200 text-red-700 px-4 py-3 rounded-xl flex items-center">
            <AlertCircle size={20} className="mr-2" />
            {error}
          </div>
        )}

        {/* Step 1: Select Activity */}
        {step === 'select' && (
          <div className="space-y-6">
            {/* Language Selection */}
            {Object.keys(supportedLanguages).length > 0 && (
              <div className="bg-white rounded-xl p-6 border-2 border-slate-100">
                <h3 className="font-bold text-[#111827] mb-3 flex items-center">
                  <Globe size={20} className="mr-2 text-[#8B5CF6]" />
                  Select Analysis Language
                </h3>
                <p className="text-sm text-[#6B7280] mb-4">Choose the language the child will speak in</p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {Object.entries(supportedLanguages).map(([code, lang]) => (
                    <button
                      key={code}
                      onClick={() => setSelectedLanguage(code)}
                      className={`p-3 rounded-lg border-2 transition-all text-center ${
                        selectedLanguage === code
                          ? 'border-[#8B5CF6] bg-purple-50'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                      data-testid={`language-${code}`}
                    >
                      <span className="font-semibold text-[#111827]">{lang.name}</span>
                      <p className="text-xs text-[#6B7280]">{lang.native_name}</p>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Milestones Info Card */}
            {milestones && (
              <div className="bg-blue-50 rounded-xl p-6 border-2 border-blue-100">
                <h3 className="font-bold text-[#111827] mb-3 flex items-center">
                  <BookOpen size={20} className="mr-2 text-blue-600" />
                  Expected Milestones for {child?.age_months} months
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                  <div>
                    <p className="font-semibold text-blue-700">Vocabulary:</p>
                    <p className="text-[#4B5563]">{milestones.vocabulary?.description || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="font-semibold text-blue-700">Sentence Length (MLU):</p>
                    <p className="text-[#4B5563]">{milestones.mlu?.description || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="font-semibold text-blue-700">Speech Rate:</p>
                    <p className="text-[#4B5563]">{milestones.speech_rate?.description || 'N/A'}</p>
                  </div>
                </div>
              </div>
            )}

            <h2 className="text-xl font-bold text-[#111827]">Select an Activity</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {activities.map((activity) => (
                <div
                  key={activity.id}
                  onClick={() => handleSelectActivity(activity)}
                  className="bg-white rounded-xl p-6 border-2 border-slate-100 hover:border-[#8B5CF6] cursor-pointer transition-all hover:shadow-md"
                  data-testid={`activity-${activity.id}`}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="font-bold text-[#111827] text-lg mb-2">{activity.title}</h3>
                      <p className="text-sm text-[#6B7280] mb-3">{activity.description}</p>
                      <div className="flex items-center text-xs text-[#9CA3AF]">
                        <Clock size={14} className="mr-1" />
                        {Math.floor(activity.duration_seconds / 60)} min
                      </div>
                    </div>
                    <ChevronRight size={20} className="text-[#9CA3AF]" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Step 2: Record */}
        {step === 'record' && selectedActivity && (
          <div className="space-y-6">
            <div className="bg-white rounded-2xl p-8 border-2 border-slate-100 text-center">
              <h2 className="text-2xl font-bold text-[#111827] mb-2">{selectedActivity.title}</h2>
              <p className="text-[#6B7280] mb-6">{selectedActivity.description}</p>
              
              {/* Activity Prompt */}
              <div className="bg-purple-50 rounded-xl p-6 mb-8 border-2 border-purple-100">
                <p className="text-lg font-semibold text-purple-800">
                  "{selectedActivity.prompt}"
                </p>
              </div>
              
              {/* Recording Controls */}
              <div className="flex flex-col items-center space-y-6">
                {/* Timer */}
                <div className="text-4xl font-mono font-bold text-[#111827]">
                  {formatTime(recordingTime)}
                </div>
                
                {/* Record Button */}
                {!audioBlob ? (
                  <button
                    onClick={isRecording ? stopRecording : startRecording}
                    className={`w-24 h-24 rounded-full flex items-center justify-center transition-all ${
                      isRecording 
                        ? 'bg-red-500 hover:bg-red-600 animate-pulse' 
                        : 'bg-[#8B5CF6] hover:bg-[#7C3AED]'
                    }`}
                    data-testid="record-button"
                  >
                    {isRecording ? (
                      <Square className="text-white" size={32} />
                    ) : (
                      <Mic className="text-white" size={32} />
                    )}
                  </button>
                ) : (
                  <div className="flex items-center space-x-4">
                    <button
                      onClick={() => {
                        setAudioBlob(null);
                        setRecordingTime(0);
                      }}
                      className="px-6 py-3 bg-slate-200 text-[#111827] rounded-xl font-semibold hover:bg-slate-300"
                    >
                      Re-record
                    </button>
                    <button
                      onClick={uploadAndAnalyze}
                      className="px-6 py-3 bg-[#10B981] text-white rounded-xl font-semibold hover:bg-[#059669] flex items-center"
                      data-testid="analyze-button"
                    >
                      <Upload size={20} className="mr-2" />
                      Analyze Speech
                    </button>
                  </div>
                )}
                
                <p className="text-sm text-[#6B7280]">
                  {isRecording ? 'Recording... Click to stop' : audioBlob ? 'Ready to analyze' : 'Click to start recording'}
                </p>
              </div>
            </div>
            
            {/* Tips */}
            <div className="bg-yellow-50 rounded-xl p-4 border border-yellow-200">
              <h4 className="font-semibold text-yellow-800 mb-2">Tips for best results:</h4>
              <ul className="text-sm text-yellow-700 space-y-1">
                <li>• Find a quiet environment</li>
                <li>• Hold the device close to the child</li>
                <li>• Encourage natural speech, not whispers</li>
                <li>• Wait for the child to respond fully</li>
              </ul>
            </div>
          </div>
        )}

        {/* Step 3: Processing */}
        {step === 'processing' && (
          <div className="bg-white rounded-2xl p-12 border-2 border-slate-100 text-center">
            <RefreshCw size={64} className="mx-auto mb-6 animate-spin text-[#8B5CF6]" />
            <h2 className="text-2xl font-bold text-[#111827] mb-2">Analyzing Speech...</h2>
            <p className="text-[#6B7280]">
              This may take a moment. We're transcribing and analyzing the speech patterns.
            </p>
          </div>
        )}

        {/* Step 4: Results */}
        {step === 'result' && result && (
          <div className="space-y-6">
            {/* Risk Level Banner */}
            <div className={`rounded-xl p-6 border-2 ${
              result.risk_level === 'high' ? 'bg-red-50 border-red-200' :
              result.risk_level === 'moderate' ? 'bg-yellow-50 border-yellow-200' :
              'bg-green-50 border-green-200'
            }`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  {getRiskIcon(result.risk_level)}
                  <div>
                    <h2 className="text-xl font-bold text-[#111827]">
                      {result.risk_level === 'high' ? 'High Risk - Action Recommended' :
                       result.risk_level === 'moderate' ? 'Moderate Risk - Monitor Closely' :
                       'Low Risk - On Track'}
                    </h2>
                    <p className="text-[#6B7280]">{result.analysis_summary}</p>
                  </div>
                </div>
                <span className={`px-4 py-2 rounded-full text-sm font-bold ${getRiskColor(result.risk_level)}`}>
                  {result.risk_level?.toUpperCase()}
                </span>
              </div>
            </div>

            {/* Transcript */}
            <div className="bg-white rounded-xl p-6 border-2 border-slate-100">
              <h3 className="font-bold text-[#111827] mb-3 flex items-center">
                <FileText size={20} className="mr-2 text-[#0F4C81]" />
                Transcript
              </h3>
              <p className="text-[#4B5563] bg-slate-50 p-4 rounded-lg italic">
                "{result.transcript}"
              </p>
            </div>

            {/* Scores */}
            {result.scores && (
              <div className="bg-white rounded-xl p-6 border-2 border-slate-100">
                <h3 className="font-bold text-[#111827] mb-4 flex items-center">
                  <Activity size={20} className="mr-2 text-[#10B981]" />
                  Linguistic Analysis Scores
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-slate-50 rounded-lg p-4 text-center">
                    <p className="text-2xl font-bold text-[#0F4C81]">{result.scores.vocabulary_count}</p>
                    <p className="text-xs text-[#6B7280]">Total Words</p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-4 text-center">
                    <p className="text-2xl font-bold text-[#8B5CF6]">{result.scores.unique_words}</p>
                    <p className="text-xs text-[#6B7280]">Unique Words</p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-4 text-center">
                    <p className="text-2xl font-bold text-[#10B981]">{result.scores.mlu?.toFixed(1) || 'N/A'}</p>
                    <p className="text-xs text-[#6B7280]">MLU</p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-4 text-center">
                    <p className="text-2xl font-bold text-[#F59E0B]">{result.scores.grammar_score}%</p>
                    <p className="text-xs text-[#6B7280]">Grammar Score</p>
                  </div>
                </div>
              </div>
            )}

            {/* Acoustic Analysis */}
            {result.scores && (result.scores.speech_rate_wpm || result.scores.pause_ratio !== undefined) && (
              <div className="bg-white rounded-xl p-6 border-2 border-purple-100">
                <h3 className="font-bold text-[#111827] mb-4 flex items-center">
                  <Zap size={20} className="mr-2 text-[#8B5CF6]" />
                  Acoustic Analysis (Speech Patterns)
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {result.scores.speech_rate_wpm && (
                    <div className="bg-purple-50 rounded-lg p-4 text-center">
                      <p className="text-2xl font-bold text-[#8B5CF6]">{result.scores.speech_rate_wpm?.toFixed(0)}</p>
                      <p className="text-xs text-[#6B7280]">Words/Minute</p>
                    </div>
                  )}
                  {result.scores.num_pauses !== undefined && (
                    <div className="bg-purple-50 rounded-lg p-4 text-center">
                      <p className="text-2xl font-bold text-[#8B5CF6]">{result.scores.num_pauses}</p>
                      <p className="text-xs text-[#6B7280]">Pause Count</p>
                    </div>
                  )}
                  {result.scores.pause_ratio !== undefined && (
                    <div className="bg-purple-50 rounded-lg p-4 text-center">
                      <p className="text-2xl font-bold text-[#8B5CF6]">{(result.scores.pause_ratio * 100).toFixed(0)}%</p>
                      <p className="text-xs text-[#6B7280]">Pause Ratio</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Recommendations */}
            {result.recommendations && result.recommendations.length > 0 && (
              <div className="bg-white rounded-xl p-6 border-2 border-slate-100">
                <h3 className="font-bold text-[#111827] mb-4 flex items-center">
                  <Award size={20} className="mr-2 text-[#F59E0B]" />
                  Recommendations
                </h3>
                <ul className="space-y-2">
                  {result.recommendations.map((rec, index) => (
                    <li key={index} className="flex items-start">
                      <CheckCircle size={16} className="mr-2 mt-1 text-[#10B981] flex-shrink-0" />
                      <span className="text-[#4B5563]">{rec}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Actions */}
            <div className="flex flex-wrap gap-4">
              <button
                onClick={() => navigate(`/speech/report/${session.session_id}`)}
                className="px-6 py-3 bg-[#0F4C81] text-white rounded-xl font-semibold hover:bg-[#0d3d6b]"
              >
                View Full Report
              </button>
              <button
                onClick={() => {
                  setStep('select');
                  setSelectedActivity(null);
                  setSession(null);
                  setAudioBlob(null);
                  setResult(null);
                  setRecordingTime(0);
                }}
                className="px-6 py-3 bg-[#8B5CF6] text-white rounded-xl font-semibold hover:bg-[#7C3AED]"
              >
                Try Another Activity
              </button>
              <button
                onClick={() => navigate(`/child/${childId}`)}
                className="px-6 py-3 bg-slate-200 text-[#111827] rounded-xl font-semibold hover:bg-slate-300"
              >
                Back to Profile
              </button>
            </div>

            {/* Disclaimer */}
            <div className="bg-slate-100 rounded-lg p-4 text-sm text-[#6B7280]">
              <strong>Disclaimer:</strong> This is a screening tool and does not replace professional diagnosis. 
              Please consult a certified Speech-Language Pathologist for comprehensive evaluation if concerns are identified.
            </div>
          </div>
        )}
      </div>
    </div>
    
    {/* Footer */}
    <footer className="bg-[#F3F4F6] border-t border-gray-200 py-3 text-center">
      <p className="text-sm text-[#6B7280]">© 2025 Hamrith Consulting Services LLP (HCS). All Rights Reserved.</p>
    </footer>
    </>
  );
};

export default SpeechAnalysis;
